import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, Check, Instagram, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AuthShell } from "@/components/layouts";
import { Field, Avatar } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { api, errMsg, uploadFile } from "@/lib/api";
import { cn } from "@/lib/utils";

const STEPS = ["Account", "Instagram", "Category", "Profile", "Submit"];
const inputCls = "h-12 rounded-xl bg-card";

function AdminSetup() {
  const { register } = useAuth();
  const { reload } = useBranding();
  const nav = useNavigate();
  const [f, setF] = useState({ full_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(f);
      await reload();
      toast.success(u.role === "admin" ? "Admin account created" : "Account created");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
      reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell>
      <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-[11px] font-bold tracking-[0.18em] px-3 py-1 mb-5" data-testid="initial-admin-setup-badge">
        <ShieldCheck className="h-3.5 w-3.5" /> INITIAL ADMIN SETUP
      </div>
      <h1 className="text-4xl font-semibold">Create the admin account</h1>
      <p className="text-sm text-muted-foreground mt-2">This is a one-time setup. The first account becomes the platform Admin; every later signup becomes a Creator.</p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-setup-form">
        <Field label="Full name"><Input required minLength={2} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className={inputCls} data-testid="setup-name-input" /></Field>
        <Field label="Email"><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="setup-email-input" /></Field>
        <Field label="Password" hint="Minimum 8 characters"><Input type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="setup-password-input" /></Field>
        <Button disabled={busy} className="w-full h-12 rounded-full font-semibold" data-testid="setup-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create admin account"}</Button>
      </form>
    </AuthShell>
  );
}

function CreatorSignup() {
  const { register } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pic, setPic] = useState(null);
  const [f, setF] = useState({ full_name: "", email: "", password: "", instagram_username: "", instagram_url: "", phone: "", category_id: "", location: "", bio: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => toast.error("Could not load categories")); }, []);

  const valid = [
    f.full_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(f.email) && f.password.length >= 8,
    /^@?[A-Za-z0-9._]{1,30}$/.test(f.instagram_username.trim()) && /instagram\.com/i.test(f.instagram_url),
    !!f.category_id,
    f.phone.trim().length >= 6,
    true,
  ];

  const submit = async () => {
    setBusy(true);
    try {
      const u = await register(f);
      if (pic) {
        try {
          const up = await uploadFile(pic);
          await api.put("/me/profile", { profile_picture: up.url });
        } catch { toast.error("Profile picture upload failed — you can add it later."); }
      }
      toast.success("Application submitted for approval");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const catName = cats.find((c) => c.id === f.category_id)?.name;

  return (
    <AuthShell wide>
      <p className="eyebrow mb-3">Creator application</p>
      <h1 className="text-4xl font-semibold">Join {branding.website_name}</h1>
      <div className="flex items-center gap-2 mt-7 mb-8" data-testid="signup-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={cn("h-1 rounded-full transition-colors", i <= step ? "gold-bg" : "bg-secondary")} />
            <p className={cn("text-[10px] mt-2 uppercase tracking-wider font-semibold", i === step ? "text-primary" : "text-muted-foreground")}>{i + 1}. {s}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 min-h-[280px]" key={step} data-testid={`signup-step-${step + 1}`}>
        {step === 0 && <>
          <Field label="Full name"><Input value={f.full_name} onChange={set("full_name")} className={inputCls} data-testid="signup-name-input" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="signup-email-input" /></Field>
          <Field label="Password" hint="Minimum 8 characters"><Input type="password" value={f.password} onChange={set("password")} className={inputCls} data-testid="signup-password-input" /></Field>
        </>}
        {step === 1 && <>
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Instagram className="h-4 w-4 text-primary" /> Only Instagram creators are supported.</div>
          <Field label="Instagram username"><Input placeholder="@username" value={f.instagram_username} onChange={set("instagram_username")} className={inputCls} data-testid="signup-instagram-username-input" /></Field>
          <Field label="Instagram profile URL"><Input placeholder="https://instagram.com/username" value={f.instagram_url} onChange={set("instagram_url")} className={inputCls} data-testid="signup-instagram-url-input" /></Field>
        </>}
        {step === 2 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="signup-category-grid">
            {cats.map((c) => (
              <button type="button" key={c.id} onClick={() => setF({ ...f, category_id: c.id })} data-testid={`signup-category-${c.name.toLowerCase()}`}
                className={cn("rounded-xl border px-4 py-3.5 text-sm text-left transition-colors",
                  f.category_id === c.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card hover:border-primary/40")}>
                {c.name}
              </button>
            ))}
          </div>
        )}
        {step === 3 && <>
          <div className="flex items-center gap-4">
            <Avatar src={pic ? URL.createObjectURL(pic) : ""} name={f.full_name} className="h-16 w-16" />
            <label className="text-sm text-primary cursor-pointer hover:underline">
              {pic ? "Change photo" : "Add profile picture"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setPic(e.target.files?.[0] || null)} data-testid="signup-profile-picture-input" />
            </label>
          </div>
          <Field label="Phone / WhatsApp"><Input value={f.phone} onChange={set("phone")} placeholder="+91" className={inputCls} data-testid="signup-phone-input" /></Field>
          <Field label="Location (optional)"><Input value={f.location} onChange={set("location")} className={inputCls} data-testid="signup-location-input" /></Field>
          <Field label="Short bio (optional)"><Textarea value={f.bio} onChange={set("bio")} className="rounded-xl bg-card" data-testid="signup-bio-input" /></Field>
        </>}
        {step === 4 && (
          <div className="uc-card p-5 space-y-3 text-sm" data-testid="signup-review">
            {[["Name", f.full_name], ["Email", f.email], ["Instagram", `@${f.instagram_username.replace(/^@/, "")}`], ["Category", catName], ["Phone", f.phone], ["Location", f.location || "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right truncate">{v}</span></div>
            ))}
            <p className="text-xs text-muted-foreground pt-3 border-t border-border">Your account will be <span className="text-primary">PENDING</span> until approved by the {branding.website_name} team.</p>
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-8">
        {step > 0 && <Button variant="outline" className="h-12 rounded-full px-6" onClick={() => setStep(step - 1)} data-testid="signup-back-button"><ArrowLeft className="h-4 w-4" /></Button>}
        {step < 4 ? (
          <Button className="flex-1 h-12 rounded-full font-semibold" disabled={!valid[step]} onClick={() => setStep(step + 1)} data-testid="signup-next-button">Continue <ArrowRight className="h-4 w-4 ml-2" /></Button>
        ) : (
          <Button className="flex-1 h-12 rounded-full font-semibold" disabled={busy} onClick={submit} data-testid="signup-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-2" />Submit for approval</>}
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-8">Already a member? <Link to="/login" className="text-primary hover:underline" data-testid="goto-login-link">Sign in</Link></p>
    </AuthShell>
  );
}

export default function Signup() {
  const { config } = useBranding();
  if (!config) return null;
  return config.setup_required ? <AdminSetup /> : <CreatorSignup />;
}
